import React from 'react';
import {
  UserCheck,
  Users,
  Book,
  CreditCard,
  DollarSign,
  Calendar,
  MessageSquare,
  Plus,
  Mail,
  FolderOpen,
  User,
  Zap,
  UserPlus,
  UserX,
  GitCommit,
  Eye,
  EyeOff,
  LayoutDashboard
} from 'lucide-react';
import { useAuth } from './auth/AuthContext';

const Dashboard = () => {
  const { user } = useAuth();

  // Mock data - in production this would come from your API
  const stats = {
    classes: 8,
    enrollments: 27,
    cancellations: '5.8k',
    newStudents: 21,
    paidInFull: 24,
    revenue: '2.8k'
  };

  const currentMonth = new Date().toLocaleDateString('en-US', { month: 'long' });

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 sm:px-6 py-4">
        <div className="flex items-center space-x-3">
          <LayoutDashboard className="w-8 h-8 text-[#7A99D8]" />
          <div>
            <h1 className="text-lg font-bold text-gray-900 dark:text-white">{user?.firstName}'s Dashboard</h1>
          </div>
        </div>
      </div>

      <div className="px-4 sm:px-6 py-4 sm:py-6">
        <div className="space-y-4 sm:space-y-6">
          {/* Stats Cards - Full Width */}
          <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-3 sm:p-4">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2 sm:gap-3 md:gap-4">
              <div className="bg-gray-200 dark:bg-gray-700 rounded-lg p-2 sm:p-2 lg:p-2 xl:p-4">
                <div className="flex flex-col items-center justify-center h-full">
                  <div className="flex items-center justify-center gap-1 mb-0.5">
                    <Book className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-gray-500 dark:text-gray-300 flex-shrink-0" />
                    <p className="text-lg sm:text-[16px] lg:text-[19px] xl:text-2xl font-bold text-gray-900 dark:text-white leading-none">{stats.classes}</p>
                  </div>
                  <p className="text-[8px] sm:text-[8.5px] lg:text-[8.5px] xl:text-[10px] text-gray-500 dark:text-gray-300">total classes</p>
                </div>
              </div>

              <div className="bg-blue-100 dark:bg-blue-900/30 rounded-lg p-2 sm:p-2 lg:p-2 xl:p-4">
                <div className="flex flex-col items-center justify-center h-full">
                  <div className="flex items-center justify-center gap-1 mb-0.5">
                    <UserCheck className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                    <p className="text-lg sm:text-[16px] lg:text-[19px] xl:text-2xl font-bold text-gray-900 dark:text-white leading-none">{stats.enrollments}</p>
                  </div>
                  <p className="text-[8px] sm:text-[8.5px] lg:text-[8.5px] xl:text-[10px] text-blue-600 dark:text-blue-400">enrollments</p>
                </div>
              </div>

              <div className="bg-red-100 dark:bg-red-900/30 rounded-lg p-2 sm:p-2 lg:p-2 xl:p-4">
                <div className="flex flex-col items-center justify-center h-full">
                  <div className="flex items-center justify-center gap-1 mb-0.5">
                    <CreditCard className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-red-600 dark:text-red-400 flex-shrink-0" />
                    <p className="text-lg sm:text-[16px] lg:text-[19px] xl:text-2xl font-bold text-[#3C3C3C] leading-none">{stats.paidInFull}</p>
                  </div>
                  <p className="text-[8px] sm:text-[8.5px] lg:text-[8.5px] xl:text-[10px] text-red-600 dark:text-red-400">payments</p>
                </div>
              </div>

              <div className="bg-cyan-100 dark:bg-cyan-900/30 rounded-lg p-2 sm:p-2 lg:p-2 xl:p-4">
                <div className="flex flex-col items-center justify-center h-full">
                  <div className="flex items-center justify-center gap-1 mb-0.5">
                    <UserPlus className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-cyan-600 dark:text-cyan-400 flex-shrink-0" />
                    <p className="text-lg sm:text-[16px] lg:text-[19px] xl:text-2xl font-bold text-[#3C3C3C] leading-none">{stats.newStudents}</p>
                  </div>
                  <p className="text-[8px] sm:text-[8.5px] lg:text-[8.5px] xl:text-[10px] text-cyan-600 dark:text-cyan-400">open seats</p>
                </div>
              </div>

              <div className="bg-purple-100 dark:bg-purple-900/30 rounded-lg p-2 sm:p-2 lg:p-2 xl:p-4">
                <div className="flex flex-col items-center justify-center h-full">
                  <div className="flex items-center justify-center gap-1 mb-0.5">
                    <DollarSign className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-purple-600 dark:text-purple-400 flex-shrink-0" />
                    <p className="text-lg sm:text-[16px] lg:text-[19px] xl:text-2xl font-bold text-[#3C3C3C] leading-none">{stats.cancellations}</p>
                  </div>
                  <p className="text-[8px] sm:text-[8.5px] lg:text-[8.5px] xl:text-[10px] text-purple-600 dark:text-purple-400">potential revenue</p>
                </div>
              </div>

              <div className="bg-green-100 dark:bg-green-900/30 rounded-lg p-2 sm:p-2 lg:p-2 xl:p-4">
                <div className="flex flex-col items-center justify-center h-full">
                  <div className="flex items-center justify-center gap-1 mb-0.5">
                    <DollarSign className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-green-600 dark:text-green-400 flex-shrink-0" />
                    <p className="text-lg sm:text-[16px] lg:text-[19px] xl:text-2xl font-bold text-[#3C3C3C] leading-none">{stats.revenue}</p>
                  </div>
                  <p className="text-[8px] sm:text-[8.5px] lg:text-[8.5px] xl:text-[10px] text-green-600 dark:text-green-400">actual revenue</p>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Actions - Mobile Only (shown after metrics) */}
          <div className="lg:hidden bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-center space-x-3 mb-4">
              <Zap className="w-4 h-4 text-gray-900 dark:text-white" />
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Quick Actions</h3>
            </div>
            
            <div className="grid grid-cols-2 gap-2">
              <button className="bg-yellow-100 dark:bg-yellow-900/30 hover:bg-yellow-200 dark:hover:bg-yellow-800/50 text-gray-900 dark:text-white px-2 py-2 rounded-lg font-medium flex items-center justify-center space-x-1 transition-colors text-xs">
                <User className="w-4 h-4 flex-shrink-0" />
                <span>Enroll Student</span>
              </button>

              <button className="bg-yellow-100 dark:bg-yellow-900/30 hover:bg-yellow-200 dark:hover:bg-yellow-800/50 text-gray-900 dark:text-white px-2 py-2 rounded-lg font-medium flex items-center justify-center space-x-1 transition-colors text-xs">
                <Plus className="w-4 h-4 flex-shrink-0" />
                <span>Add Class</span>
              </button>

              <button className="bg-yellow-100 dark:bg-yellow-900/30 hover:bg-yellow-200 dark:hover:bg-yellow-800/50 text-gray-900 dark:text-white px-2 py-2 rounded-lg font-medium flex items-center justify-center space-x-1 transition-colors text-xs">
                <Mail className="w-4 h-4 flex-shrink-0" />
                <span>Send Message</span>
              </button>

              <button className="bg-yellow-100 dark:bg-yellow-900/30 hover:bg-yellow-200 dark:hover:bg-yellow-800/50 text-gray-900 dark:text-white px-2 py-2 rounded-lg font-medium flex items-center justify-center space-x-1 transition-colors text-xs">
                <FolderOpen className="w-4 h-4 flex-shrink-0" />
                <span>Add Project</span>
              </button>

              <button className="bg-yellow-100 dark:bg-yellow-900/30 hover:bg-yellow-200 dark:hover:bg-yellow-800/50 text-gray-900 dark:text-white px-2 py-2 rounded-lg font-medium flex items-center justify-center space-x-1 transition-colors text-xs">
                <CreditCard className="w-4 h-4 flex-shrink-0" />
                <span>Request Payment</span>
              </button>

              <button className="bg-yellow-100 dark:bg-yellow-900/30 hover:bg-yellow-200 dark:hover:bg-yellow-800/50 text-gray-900 dark:text-white px-2 py-2 rounded-lg font-medium flex items-center justify-center space-x-1 transition-colors text-xs">
                <Eye className="w-4 h-4 flex-shrink-0" />
                <span>View Portal</span>
              </button>
            </div>
          </div>

          {/* Main Content Grid */}
          <div className="space-y-6">

              {/* Content Sections */}
              <div className="grid grid-cols-1 lg:grid-cols-3 xl:grid-cols-5 gap-2 lg:gap-3">
                {/* Upcoming Classes - 1/3 at 1024px, 2/5 (40%) at 1280px+ */}
                <div className="lg:col-span-1 xl:col-span-2 bg-white rounded-lg border border-gray-200 p-6 lg:p-3 xl:p-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <Calendar className="w-4 h-4 lg:w-5 lg:h-5 text-[#3C3C3C]" />
                    <h2 className="text-sm lg:text-sm xl:text-lg font-semibold text-[#3C3C3C]">Upcoming Classes</h2>
                  </div>
                  
                  {/* Simple Calendar View */}
                  <div className="space-y-4">
                    <div className="grid grid-cols-7 gap-2 text-center">
                      {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
                        <div key={day} className="text-xs font-medium text-[#3C3C3C] py-2">
                          {day}
                        </div>
                      ))}
                    </div>
                    
                    <div className="grid grid-cols-7 gap-2 text-center">
                      {/* Sample calendar dates */}
                      {[26, 27, 28, 29, 30, 31, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 1, 2, 3, 4, 5, 6].map((date, index) => (
                        <div key={index} className={`text-xs py-2 ${
                          index < 6 || index > 35 ? 'text-gray-400' : 'text-[#3C3C3C]'
                        } ${[8, 12, 19].includes(date) && index >= 6 && index <= 35 ? 'bg-[#C5EFFF] rounded font-medium' : ''}`}>
                          {date}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Recent Enrollments - 1/3 at 1024px, 2/5 (40%) at 1280px+ */}
                <div className="lg:col-span-1 xl:col-span-2 bg-white rounded-lg border border-gray-200 p-6 lg:p-3 xl:p-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <UserPlus className="w-4 h-4 lg:w-5 lg:h-5 text-[#3C3C3C]" />
                    <h2 className="text-sm lg:text-sm xl:text-lg font-semibold text-[#3C3C3C]">Recent Enrollments</h2>
                  </div>
                  <div className="space-y-3">
                    <div className="p-3 border border-gray-200 rounded-lg">
                      <div className="flex justify-between items-start mb-1">
                        <p className="text-sm lg:text-[12px] xl:text-sm font-medium text-[#3C3C3C]">Emma Johnson</p>
                        <span className="text-xs lg:text-[9px] xl:text-xs px-2 py-1 lg:px-1 lg:py-0.5 xl:px-2 xl:py-1 bg-[#B7F1B6] text-[#3C3C3C] rounded-full">Paid</span>
                      </div>
                      <p className="text-xs lg:text-[11px] xl:text-xs text-[#3C3C3C] mb-1">Beginning Sewing - 3 days ago</p>
                      <div className="flex justify-between text-xs lg:text-[11px] xl:text-xs text-gray-600">
                        <span>Jan 15 - Feb 15</span>
                        <span className="font-medium">$179</span>
                      </div>
                    </div>
                    <div className="p-3 border border-gray-200 rounded-lg">
                      <div className="flex justify-between items-start mb-1">
                        <p className="text-sm lg:text-[12px] xl:text-sm font-medium text-[#3C3C3C]">Michael Chen</p>
                        <span className="text-xs lg:text-[9px] xl:text-xs px-2 py-1 lg:px-1 lg:py-0.5 xl:px-2 xl:py-1 bg-[#FFECC5] text-[#3C3C3C] rounded-full">Pending</span>
                      </div>
                      <p className="text-xs lg:text-[11px] xl:text-xs text-[#3C3C3C] mb-1">Pattern Making 101 - 5 days ago</p>
                      <div className="flex justify-between text-xs lg:text-[11px] xl:text-xs text-gray-600">
                        <span>Jan 20 - Mar 20</span>
                        <span className="font-medium">$249</span>
                      </div>
                    </div>
                    <div className="p-3 border border-gray-200 rounded-lg">
                      <div className="flex justify-between items-start mb-1">
                        <p className="text-sm lg:text-[12px] xl:text-sm font-medium text-[#3C3C3C]">Sofia Rodriguez</p>
                        <span className="text-xs lg:text-[9px] xl:text-xs px-2 py-1 lg:px-1 lg:py-0.5 xl:px-2 xl:py-1 bg-[#B7F1B6] text-[#3C3C3C] rounded-full">Paid</span>
                      </div>
                      <p className="text-xs lg:text-[11px] xl:text-xs text-[#3C3C3C] mb-1">Quilting Basics - 1 week ago</p>
                      <div className="flex justify-between text-xs lg:text-[11px] xl:text-xs text-gray-600">
                        <span>Jan 10 - Feb 28</span>
                        <span className="font-medium">$159</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Quick Actions - Hidden on mobile/tablet, 1/3 at 1024px, 1/5 (20%) at 1280px+ */}
                <div className="hidden lg:block lg:col-span-1 xl:col-span-1 bg-white rounded-lg border border-gray-200 p-6 lg:p-3 xl:p-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <Zap className="w-4 h-4 lg:w-5 lg:h-5 text-[#3C3C3C]" />
                    <h3 className="text-sm lg:text-sm xl:text-lg font-semibold text-[#3C3C3C]">Quick Actions</h3>
                  </div>
                  
                  <div className="space-y-3">
                    <button className="w-full bg-[#FFECC5] hover:bg-[#FFE4A3] text-[#3C3C3C] px-3 sm:px-2 lg:px-3 py-2 rounded-lg font-medium flex items-center justify-start space-x-2 sm:space-x-1 lg:space-x-2 transition-colors text-xs lg:text-[10px] xl:text-xs">
                      <User className="w-4 h-4 sm:w-3 sm:h-3 lg:w-4 lg:h-4 flex-shrink-0" />
                      <span>Enroll a Student</span>
                    </button>

                    <button className="w-full bg-[#FFECC5] hover:bg-[#FFE4A3] text-[#3C3C3C] px-3 sm:px-2 lg:px-3 py-2 rounded-lg font-medium flex items-center justify-start space-x-2 sm:space-x-1 lg:space-x-2 transition-colors text-xs lg:text-[10px] xl:text-xs">
                      <Plus className="w-4 h-4 sm:w-3 sm:h-3 lg:w-4 lg:h-4 flex-shrink-0" />
                      <span>Add a Class</span>
                    </button>

                    <button className="w-full bg-[#FFECC5] hover:bg-[#FFE4A3] text-[#3C3C3C] px-3 sm:px-2 lg:px-3 py-2 rounded-lg font-medium flex items-center justify-start space-x-2 sm:space-x-1 lg:space-x-2 transition-colors text-xs lg:text-[10px] xl:text-xs">
                      <Mail className="w-4 h-4 sm:w-3 sm:h-3 lg:w-4 lg:h-4 flex-shrink-0" />
                      <span>Send a Message</span>
                    </button>

                    <button className="w-full bg-[#FFECC5] hover:bg-[#FFE4A3] text-[#3C3C3C] px-3 sm:px-2 lg:px-3 py-2 rounded-lg font-medium flex items-center justify-start space-x-2 sm:space-x-1 lg:space-x-2 transition-colors text-xs lg:text-[10px] xl:text-xs">
                      <FolderOpen className="w-4 h-4 sm:w-3 sm:h-3 lg:w-4 lg:h-4 flex-shrink-0" />
                      <span>Add a Project</span>
                    </button>

                    <button className="w-full bg-[#FFECC5] hover:bg-[#FFE4A3] text-[#3C3C3C] px-3 sm:px-2 lg:px-3 py-2 rounded-lg font-medium flex items-center justify-start space-x-2 sm:space-x-1 lg:space-x-2 transition-colors text-xs lg:text-[10px] xl:text-xs">
                      <CreditCard className="w-4 h-4 sm:w-3 sm:h-3 lg:w-4 lg:h-4 flex-shrink-0" />
                      <span>Request Payment</span>
                    </button>

                    <button className="w-full bg-[#FFECC5] hover:bg-[#FFE4A3] text-[#3C3C3C] px-3 sm:px-2 lg:px-3 py-2 rounded-lg font-medium flex items-center justify-start space-x-2 sm:space-x-1 lg:space-x-2 transition-colors text-xs lg:text-[10px] xl:text-xs">
                      <Eye className="w-4 h-4 sm:w-3 sm:h-3 lg:w-4 lg:h-4 flex-shrink-0" />
                      <span>View Portal</span>
                    </button>
                  </div>
                </div>
              </div>

          {/* Bottom Sections - Full Width 50/50 Split */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Recent Messages */}
            <div className="bg-white rounded-lg border border-gray-200 p-6 lg:p-3 xl:p-6">
              <div className="flex items-center space-x-3 mb-4">
                <MessageSquare className="w-4 h-4 lg:w-5 lg:h-5 text-[#3C3C3C]" />
                <h2 className="text-sm lg:text-sm xl:text-lg font-semibold text-[#3C3C3C]">Recent Messages</h2>
              </div>
              <div className="space-y-3">
                <div className="p-3 border border-gray-200 rounded-lg">
                  <div className="flex justify-between items-start mb-1">
                    <div className="flex items-center space-x-2">
                      <EyeOff className="w-4 h-4 text-gray-400" />
                      <p className="text-sm lg:text-[12px] xl:text-sm font-extrabold text-[#3C3C3C]">Question about Fabric Types</p>
                    </div>
                    <span className="text-xs lg:text-[11px] xl:text-xs text-gray-600">2 hours ago</span>
                  </div>
                  <p className="text-xs lg:text-[11px] xl:text-xs text-gray-600 mb-1">From: sarah.parker@email.com</p>
                  <p className="text-xs lg:text-[11px] xl:text-xs text-[#3C3C3C] line-clamp-2">Hi, I'm wondering what types of fabric we'll be working with in the Beginning Sewing class. Should I bring my own...</p>
                </div>
                <div className="p-3 border border-gray-200 rounded-lg">
                  <div className="flex justify-between items-start mb-1">
                    <div className="flex items-center space-x-2">
                      <Eye className="w-4 h-4 text-[#7A99D8]" />
                      <p className="text-sm lg:text-[12px] xl:text-sm font-medium text-[#3C3C3C]">Sewing Machine Question</p>
                    </div>
                    <span className="text-xs lg:text-[11px] xl:text-xs text-gray-600">1 day ago</span>
                  </div>
                  <p className="text-xs lg:text-[11px] xl:text-xs text-gray-600 mb-1">From: james.wilson@email.com</p>
                  <p className="text-xs lg:text-[11px] xl:text-xs text-[#3C3C3C] line-clamp-2">Do I need to bring my own sewing machine to the Pattern Making class, or will machines be provided? Also wondering about...</p>
                </div>
                <div className="p-3 border border-gray-200 rounded-lg">
                  <div className="flex justify-between items-start mb-1">
                    <div className="flex items-center space-x-2">
                      <Eye className="w-4 h-4 text-[#7A99D8]" />
                      <p className="text-sm lg:text-[12px] xl:text-sm font-medium text-[#3C3C3C]">Thank You!</p>
                    </div>
                    <span className="text-xs text-gray-600">3 days ago</span>
                  </div>
                  <p className="text-xs text-gray-600 mb-1">From: emily.chen@email.com</p>
                  <p className="text-xs text-[#3C3C3C] line-clamp-2">Thank you for the wonderful Embroidery Basics class! My daughter finished her first project and is so proud...</p>
                </div>
              </div>
            </div>

            {/* Recent Payments */}
            <div className="bg-white rounded-lg border border-gray-200 p-6 lg:p-3 xl:p-6">
              <div className="flex items-center space-x-3 mb-4">
                <CreditCard className="w-4 h-4 lg:w-5 lg:h-5 text-[#3C3C3C]" />
                <h2 className="text-sm lg:text-sm xl:text-lg font-semibold text-[#3C3C3C]">Recent Payments</h2>
              </div>
              <div className="space-y-3">
                <div className="p-3 border border-gray-200 rounded-lg">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center space-x-3">
                      <GitCommit className="w-10 h-10 text-[#7A99D8]" />
                      <div>
                        <p className="text-sm lg:text-[12px] xl:text-sm font-medium text-[#3C3C3C]">Emma Johnson</p>
                        <p className="text-xs lg:text-[11px] xl:text-xs text-[#3C3C3C]">Beginning Sewing</p>
                        <p className="text-xs lg:text-[11px] xl:text-xs text-gray-600">Jan 15, 2024</p>
                      </div>
                    </div>
                    <p className="text-lg lg:text-[16px] xl:text-lg font-bold text-[#3C3C3C]">$179</p>
                  </div>
                </div>
                <div className="p-3 border border-gray-200 rounded-lg">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center space-x-3">
                      <Book className="w-8 h-8 text-[#72CD7D]" />
                      <div>
                        <p className="text-sm lg:text-[12px] xl:text-sm font-medium text-[#3C3C3C]">Sofia Rodriguez</p>
                        <p className="text-xs lg:text-[11px] xl:text-xs text-[#3C3C3C]">Quilting Basics</p>
                        <p className="text-xs lg:text-[11px] xl:text-xs text-gray-600">Jan 12, 2024</p>
                      </div>
                    </div>
                    <p className="text-lg lg:text-[16px] xl:text-lg font-bold text-[#3C3C3C]">$159</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  );
};

export default Dashboard;